import React, { useState, useEffect } from "react";
import {
  Stack,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Typography,
} from "@mui/material";
import AppModal from "../common/AppModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { applyToInvoice } from "../../redux/actions/creditNoteActions";
import { getInvoices } from "../../redux/actions/invoiceActions";
import { formatCurrency } from "../../utils/currencyUtils";
import { CreditNoteResponseDto } from "../../shared/dtos/creditNote/CreditNoteResponseDto";
import { InvoiceSummaryDto } from "../../shared/dtos/invoice/InvoiceSummaryDto"; // Assuming this DTO exists

interface ApplyToInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  creditNote: CreditNoteResponseDto;
}

const ApplyToInvoiceModal: React.FC<ApplyToInvoiceModalProps> = ({
  open,
  onClose,
  creditNote,
}) => {
  const dispatch = useAppDispatch();
  const { loadingApply: loadingApplyCreditNote } = useAppSelector(
    (state) => state.creditNote,
  );
  const { items: invoices } = useAppSelector((state) => state.invoice);

  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceSummaryDto | null>(null);

  useEffect(() => {
    // Fetch open invoices for this specific customer
    if (open && creditNote.customerId) {
      dispatch(getInvoices({ size: 20, search: creditNote.customerName }));
    }
  }, [open, creditNote, dispatch]);

  const handleApply = (): void => {
    if (selectedInvoice) {
      dispatch(applyToInvoice(creditNote.id, selectedInvoice.id));
    }
  };

  const availableAmount = creditNote.total - creditNote.amountUsed;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Apply C.N ${creditNote.noteNumber} to Invoice`}
      width="sm"
      loading={loadingApplyCreditNote}
    >
      <Stack spacing={3}>
        <Alert severity="info">
          Available Credit:{" "}
          <Typography sx={{ fontWeight: "bold" }}>
            {formatCurrency(availableAmount)}
          </Typography>
        </Alert>

        <Autocomplete
          options={invoices || []}
          getOptionLabel={(opt) =>
            `${opt.invoiceNumber} - Due: ${formatCurrency(opt.total - opt.amountPaid)}`
          }
          value={selectedInvoice}
          onChange={(_e, val) => setSelectedInvoice(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Unpaid Invoice"
              variant="outlined"
            />
          )}
        />

        <Button
          variant="contained"
          color="secondary"
          disabled={!selectedInvoice || loadingApplyCreditNote}
          onClick={handleApply}
        >
          Apply Credit
        </Button>
      </Stack>
    </AppModal>
  );
};

export default ApplyToInvoiceModal;
